import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '@store';
import {ISnapshot, ISystem, ISystemNotificationsSettings, IUser} from '@types';
import {IDLE, PENDING} from '@constants/loadingState';
import {systemsAPI} from '@api/methods';

interface SystemsState {
  loading: string;
  editSystemLoading: string;
  deleteSystemLoading: string;
  addLoading: string;
  editLoading: string;
  deleteLoading: string;
  getSnapshotLoading: string;
  getNotificationSettings: string;
  setNotificationSettings: string;
  systems: ISystem[];
  notificationSettings: ISystemNotificationsSettings;
}

const initialState: SystemsState = {
  loading: IDLE,
  editSystemLoading: IDLE,
  deleteSystemLoading: IDLE,
  addLoading: IDLE,
  editLoading: IDLE,
  deleteLoading: IDLE,
  getSnapshotLoading: IDLE,
  getNotificationSettings: IDLE,
  setNotificationSettings: IDLE,
  systems: [],
  notificationSettings: {
    interval_type: 3,
    notification_type: [],
  },
};

export const getSystems = createAsyncThunk<ISystem[], any, {state: RootState}>(
  'systems/get/all',
  async () => {
    return await systemsAPI.getSystems();
  },
);

export const addSystem = createAsyncThunk<
  string,
  {name: string; system_admin_id: string},
  {state: RootState}
>('systems/add/new', async ({name, system_admin_id}) => {
  return await systemsAPI.addSystem(name, system_admin_id);
});

export const editSystem = createAsyncThunk<
  string,
  {name: string; system_admin_id: string; system_id: number},
  {state: RootState}
>('systems/edit', async ({name, system_admin_id, system_id}) => {
  return await systemsAPI.editSystem(name, system_admin_id, system_id);
});

export const deleteSystem = createAsyncThunk<string, number, {state: RootState}>(
  'systems/delete',
  async system_id => {
    return await systemsAPI.deleteSystem(system_id);
  },
);

export const getSystemSnapshot = createAsyncThunk<
  ISnapshot,
  {system_id: number; snapshot_id?: number},
  {state: RootState}
>('systems/get/snapshot', async ({system_id, snapshot_id}) => {
  return await systemsAPI.getSystemSnapshot(system_id, snapshot_id);
});

export const addSubsystem = createAsyncThunk<
  string,
  {
    name: string;
    system_id: number;
    users: IUser[];
  },
  {state: RootState}
>('subsystems/add/new', async ({name, system_id, users}) => {
  return await systemsAPI.addSubsystem(system_id, name, users);
});

export const editSubsystem = createAsyncThunk<
  string,
  {
    name: string;
    subsystem_id: number;
    users: IUser[];
  },
  {state: RootState}
>('subsystems/edit', async ({name, subsystem_id, users}) => {
  return await systemsAPI.editSubsystem(subsystem_id, name, users);
});

export const deleteSubsystem = createAsyncThunk<string, number, {state: RootState}>(
  'subsystems/delete',
  async subsystem_id => {
    return await systemsAPI.deleteSubsystem(subsystem_id);
  },
);

export const addModule = createAsyncThunk<
  string,
  {
    name: string;
    subsystem_id: number;
    test_types_ids: number[];
  },
  {state: RootState}
>('module/add/new', async ({name, subsystem_id, test_types_ids}) => {
  return await systemsAPI.addModule(subsystem_id, name, test_types_ids);
});

export const deleteModule = createAsyncThunk<string, number, {state: RootState}>(
  'module/delete',
  async module_id => {
    return await systemsAPI.deleteModule(module_id);
  },
);

export const editModule = createAsyncThunk<
  string,
  {
    module_id: number;
    name: string;
    delete_test_types_ids: number[];
    add_test_types_ids: number[];
  },
  {state: RootState}
>('module/edit', async ({module_id, name, delete_test_types_ids, add_test_types_ids}) => {
  return await systemsAPI.editModule(
    module_id,
    name,
    delete_test_types_ids,
    add_test_types_ids,
  );
});

export const addCell = createAsyncThunk<
  string,
  {
    coverage: number;
    auto_coverage: number;
    module_id: number;
    test_type_id: number;
    is_changeable: boolean;
  },
  {state: RootState}
>('cell/add', async cellData => {
  return await systemsAPI.addCell(cellData);
});

export const updateSystemUsers = createAsyncThunk<
  string,
  {
    system_id: number;
    users: {
      id: number;
      scoped_role_id: number;
    }[];
  },
  {state: RootState}
>('system/users/update', async data => {
  return await systemsAPI.updateSystemUsers(data.system_id, data.users);
});

export const getSystemNotificationsSettings = createAsyncThunk<
  ISystemNotificationsSettings,
  {system_id: number},
  {state: RootState}
>('systems/get/notification/settings', async ({system_id}) => {
  return await systemsAPI.getSystemNotificationSettings(system_id);
});

export const setSystemNotificationsSettings = createAsyncThunk<
  ISystemNotificationsSettings,
  {system_id: number; interval_type: number; notification_type: number[]},
  {state: RootState}
>(
  'systems/set/notification/settings',
  async ({system_id, interval_type, notification_type}) => {
    return await systemsAPI.setSystemNotificationSettings(
      system_id,
      interval_type,
      notification_type,
    );
  },
);

export const deleteSystemNotificationsSettings = createAsyncThunk<
  ISystemNotificationsSettings,
  {system_id: number},
  {state: RootState}
>('systems/delete/notification/settings', async ({system_id}) => {
  return await systemsAPI.deleteSystemNotificationSettings(system_id);
});

const systemsSlice = createSlice({
  name: 'systems',
  initialState,
  reducers: {
    addUsersToSystem(
      state,
      action: PayloadAction<{
        systemId: number;
        users: IUser[];
      }>,
    ) {
      const {systemId, users} = action.payload;
      const system = state.systems.find(sys => sys.id === systemId);
      if (!system) return;
      system.users = users;
    },
    addTemporaryCell(
      state,
      action: PayloadAction<{
        system_id: number;
        subsystem_id: number;
        module_id: number;
        test_type_id: number;
        coverage: number;
        auto_coverage: number;
      }>,
    ) {
      const {system_id, subsystem_id, module_id, test_type_id, coverage, auto_coverage} =
        action.payload;
      const system = state.systems.find(sys => sys.id === system_id);
      const subsystem = system?.snapshot?.subsystems.find(
        subsys => subsys.id === subsystem_id,
      );
      const module = subsystem?.modules.find(module => module.id === module_id);
      const cell = module?.cells?.find(cell => cell.test_type_id === test_type_id);
      if (cell) {
        cell.coverage = coverage;
        cell.auto_coverage = auto_coverage;
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(getSystems.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(getSystems.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(getSystems.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.systems = payload;
    });
    builder.addCase(addSystem.pending, state => {
      state.addLoading = PENDING;
    });
    builder.addCase(addSystem.rejected, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(addSystem.fulfilled, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(editSystem.pending, state => {
      state.editSystemLoading = PENDING;
    });
    builder.addCase(editSystem.rejected, state => {
      state.editSystemLoading = IDLE;
    });
    builder.addCase(editSystem.fulfilled, state => {
      state.editSystemLoading = IDLE;
    });
    builder.addCase(deleteSystem.pending, state => {
      state.deleteSystemLoading = PENDING;
    });
    builder.addCase(deleteSystem.rejected, state => {
      state.deleteSystemLoading = IDLE;
    });
    builder.addCase(deleteSystem.fulfilled, state => {
      state.deleteSystemLoading = IDLE;
    });
    builder.addCase(getSystemSnapshot.pending, state => {
      state.getSnapshotLoading = PENDING;
    });
    builder.addCase(getSystemSnapshot.rejected, (state, action) => {
      state.getSnapshotLoading = IDLE;
      console.error('Failed to get system snapshot:', action.error);
    });
    builder.addCase(getSystemSnapshot.fulfilled, (state, action) => {
      const systemId = action.meta.arg.system_id;
      const snapshot = action.payload;
      const system = state.systems.find(sys => sys.id === systemId);
      if (system) {
        system.snapshot = snapshot;
      }
      state.getSnapshotLoading = IDLE;
    });
    builder.addCase(addSubsystem.pending, state => {
      state.addLoading = PENDING;
    });
    builder.addCase(addSubsystem.rejected, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(addSubsystem.fulfilled, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(editSubsystem.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(editSubsystem.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(editSubsystem.fulfilled, state => {
      state.loading = IDLE;
    });
    builder.addCase(deleteSubsystem.pending, state => {
      state.deleteLoading = PENDING;
    });
    builder.addCase(deleteSubsystem.rejected, state => {
      state.deleteLoading = IDLE;
    });
    builder.addCase(deleteSubsystem.fulfilled, state => {
      state.deleteLoading = IDLE;
    });
    builder.addCase(addModule.pending, state => {
      state.addLoading = PENDING;
    });
    builder.addCase(addModule.rejected, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(addModule.fulfilled, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(deleteModule.pending, state => {
      state.deleteLoading = PENDING;
    });
    builder.addCase(deleteModule.rejected, state => {
      state.deleteLoading = IDLE;
    });
    builder.addCase(deleteModule.fulfilled, state => {
      state.deleteLoading = IDLE;
    });
    builder.addCase(editModule.pending, state => {
      state.editLoading = PENDING;
    });
    builder.addCase(editModule.rejected, state => {
      state.editLoading = IDLE;
    });
    builder.addCase(editModule.fulfilled, state => {
      state.editLoading = IDLE;
    });
    builder.addCase(addCell.pending, state => {
      state.addLoading = PENDING;
    });
    builder.addCase(addCell.rejected, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(addCell.fulfilled, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(updateSystemUsers.pending, state => {
      state.addLoading = PENDING;
    });
    builder.addCase(updateSystemUsers.rejected, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(updateSystemUsers.fulfilled, state => {
      state.addLoading = IDLE;
    });
    builder.addCase(getSystemNotificationsSettings.pending, state => {
      state.getNotificationSettings = PENDING;
    });
    builder.addCase(getSystemNotificationsSettings.rejected, state => {
      state.getNotificationSettings = IDLE;
      state.notificationSettings = {notification_type: [], interval_type: 3};
    });
    builder.addCase(getSystemNotificationsSettings.fulfilled, (state, action) => {
      state.getNotificationSettings = IDLE;
      state.notificationSettings = {
        notification_type: action.payload.notification_type,
        interval_type: action.payload.interval_type,
      };
    });
    builder.addCase(setSystemNotificationsSettings.pending, state => {
      state.setNotificationSettings = PENDING;
    });
    builder.addCase(setSystemNotificationsSettings.rejected, state => {
      state.setNotificationSettings = IDLE;
    });
    builder.addCase(setSystemNotificationsSettings.fulfilled, (state, action) => {
      state.setNotificationSettings = IDLE;
      state.notificationSettings = {
        notification_type: action.payload.notification_type,
        interval_type: action.payload.interval_type,
      };
    });
    builder.addCase(deleteSystemNotificationsSettings.pending, state => {
      state.setNotificationSettings = PENDING;
    });
    builder.addCase(deleteSystemNotificationsSettings.rejected, state => {
      state.setNotificationSettings = IDLE;
    });
    builder.addCase(deleteSystemNotificationsSettings.fulfilled, state => {
      state.setNotificationSettings = IDLE;
      state.notificationSettings = {notification_type: [], interval_type: 3};
    });
  },
});

export const systems = (state: RootState) => state.systems.systems;

export const {addUsersToSystem, addTemporaryCell} = systemsSlice.actions;
export default systemsSlice.reducer;
